/**
 * @file App.jsx
 * Radice dell'applicazione: definisce router, provider di contesto e lazy loading delle pagine.
 *
 * - Tutti i provider (App, Data, Deadline, Ui, Theme) wrappano l'intera app.
 * - Le pagine sono caricate lazily per ridurre il bundle iniziale, tramite
 *   lazyWithReload (vedi lib/utils/lazyWithReload.js) che gestisce i chunk
 *   non piu' disponibili dopo un nuovo deploy.
 * - RootLayout gestisce AppShell + Suspense per il fallback di caricamento.
 * - La navigazione usa createBrowserRouter (React Router v6.4+).
 */

import { Suspense, useContext } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom';
import { AppContext, AppProvider } from './context/AppContext';
import { ConfigProvider } from './context/ConfigContext';
import { DataProvider } from './context/DataContext';
import { DeadlineProvider } from './context/DeadlineContext';
import { UiProvider } from './context/UiContext';
import { ThemeProvider } from './context/ThemeContext';
import { PermissionsProvider } from './context/PermissionsContext';
import AppShell from './components/layout/AppShell';
import RoleGuard from './components/shared/RoleGuard';
import { ROLES } from './lib/constants/roles';
import { ROUTES } from './lib/constants/routes';
import LoginPage from './pages/LoginPage';
import { lazyWithReload } from './lib/utils/lazyWithReload';

// Pagine caricate a richiesta: ognuna diventa un chunk separato (/assets/<Nome>-<hash>.js).
// Si usa lazyWithReload e non React.lazy: se dopo un deploy il chunk della versione
// precedente non esiste piu', la pagina viene ricaricata una volta invece di mostrare
// "Unexpected Application Error". Le nuove pagine lazy vanno dichiarate allo stesso modo.
const PlanningPage = lazyWithReload(() => import("./pages/PlanningPage"));
const ExcelPage = lazyWithReload(() => import("./pages/ExcelPage"));
const MergePage = lazyWithReload(() => import("./pages/MergePage"));
const MergeGridPage = lazyWithReload(() => import("./pages/MergeGridPage"));
const MergePublishedPage = lazyWithReload(() => import("./pages/MergePublishedPage"));
const DashboardPage = lazyWithReload(() => import("./pages/DashboardPage"));
const BuDashboardPage = lazyWithReload(() => import("./pages/BuDashboardPage"));
const CriticalPage = lazyWithReload(() => import("./pages/CriticalPage"));
const SetupPage = lazyWithReload(() => import("./pages/SetupPage"));
const RisorsePage = lazyWithReload(() => import("./pages/RisorsePage"));
const BusinessUnitPage = lazyWithReload(() => import("./pages/BusinessUnitPage"));
const SedePage = lazyWithReload(() => import("./pages/SedePage"));
const ClientiPage = lazyWithReload(() => import("./pages/ClientiPage"));
const AreePage = lazyWithReload(() => import("./pages/AreePage"));
const DateLimitePage = lazyWithReload(() => import("./pages/DateLimitePage"));
const WorkbenchPage = lazyWithReload(() => import("./pages/WorkbenchPage"));
const NotFoundPage = lazyWithReload(() => import("./pages/NotFoundPage"));
const ProjectsPage = lazyWithReload(() => import("./pages/ProjectsPage"));
const TasksPage = lazyWithReload(() => import("./pages/TasksPage"));
const TaskResourceLinksPage = lazyWithReload(() => import("./pages/TaskResourceLinksPage"));
const TaskResourcePatternsPage = lazyWithReload(() => import("./pages/TaskResourcePatternsPage"));
const ProjectManagersPage = lazyWithReload(() => import("./pages/ProjectManagersPage"));
const SeniorManagementPage = lazyWithReload(() => import("./pages/SeniorManagementPage"));
const NonWorkingDaysPage = lazyWithReload(() => import("./pages/NonWorkingDaysPage"));

// Tutti i ruoli operativi tranne SalesBackoffice, che vede solo la pagina Clienti.
const NON_SALES_BACKOFFICE = [ROLES.ADMIN, ROLES.PLANNER, ROLES.PM_RICHIEDENTE, ROLES.RICHIEDENTE, ROLES.HR, ROLES.USER];

function Fallback() {
  return (
    <div className="flex items-center justify-center h-full p-8 text-ink-400 dark:text-ink-500">
      Caricamento...
    </div>
  );
}

function RootLayout() {
  return (
    <AppShell>
      <Suspense fallback={<Fallback />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      { index: true, element: <Navigate to="/pianificazione-definitiva" replace /> },
      {
        path: "planning",
        element: (
          <RoleGuard allow={[ROLES.ADMIN, ROLES.PM_RICHIEDENTE, ROLES.RICHIEDENTE, ROLES.HR]}>
            <PlanningPage />
          </RoleGuard>
        ),
      },
      {
        path: "excel",
        element: (
          <RoleGuard
            allow={[ROLES.ADMIN, ROLES.PM_RICHIEDENTE, ROLES.RICHIEDENTE, ROLES.HR]}
            fallback={ROUTES.PIANIFICAZIONE_DEFINITIVA}
          >
            <ExcelPage />
          </RoleGuard>
        ),
      },
      {
        path: "excel/:headerId",
        element: (
          <RoleGuard
            allow={[ROLES.ADMIN, ROLES.PM_RICHIEDENTE, ROLES.RICHIEDENTE, ROLES.HR]}
            fallback={ROUTES.PIANIFICAZIONE_DEFINITIVA}
          >
            <ExcelPage />
          </RoleGuard>
        ),
      },
      {
        path: "merge",
        element: (
          <RoleGuard permission="canMerge">
            <MergePage />
          </RoleGuard>
        ),
      },
      {
        path: "merge/:headerId",
        element: (
          <RoleGuard permission="canMerge">
            <MergeGridPage />
          </RoleGuard>
        ),
      },
      {
        path: "merge-grid",
        element: (
          <RoleGuard permission="canMerge">
            <MergeGridPage />
          </RoleGuard>
        ),
      },
      {
        path: "published-merge",
        element: (
          <RoleGuard allow={NON_SALES_BACKOFFICE}>
            <MergePublishedPage />
          </RoleGuard>
        ),
      },
      {
        path: "pianificazione-definitiva",
        element: (
          <RoleGuard allow={NON_SALES_BACKOFFICE}>
            <MergePublishedPage />
          </RoleGuard>
        ),
      },
      {
        path: "pianificazione-definitiva/:headerId",
        element: (
          <RoleGuard allow={NON_SALES_BACKOFFICE}>
            <MergePublishedPage />
          </RoleGuard>
        ),
      },
      {
        path: "dashboard",
        element: (
          <RoleGuard permission="canSeeDashboard">
            <DashboardPage />
          </RoleGuard>
        ),
      },
      {
        path: "dashboard-bu",
        element: (
          <RoleGuard permission="canSeeDashboardBu">
            <BuDashboardPage />
          </RoleGuard>
        ),
      },
      {
        path: "critical",
        element: (
          <RoleGuard permission="canSeeCritical">
            <CriticalPage />
          </RoleGuard>
        ),
      },
      {
        path: "setup/risorse",
        element: (
          <RoleGuard allow={[ROLES.ADMIN, ROLES.HR]}>
            <RisorsePage />
          </RoleGuard>
        ),
      },
      {
        path: "setup/business-units",
        element: (
          <RoleGuard allow={[ROLES.ADMIN, ROLES.HR]}>
            <BusinessUnitPage />
          </RoleGuard>
        ),
      },
      {
        path: "setup/sedi",
        element: (
          <RoleGuard allow={[ROLES.ADMIN, ROLES.HR]}>
            <SedePage />
          </RoleGuard>
        ),
      },
      {
        path: "setup/clienti",
        element: (
          <RoleGuard allow={[ROLES.ADMIN, ROLES.PLANNER, ROLES.PM_RICHIEDENTE, ROLES.RICHIEDENTE, ROLES.HR, ROLES.USER, ROLES.SALES_BACKOFFICE]}>
            <ClientiPage />
          </RoleGuard>
        ),
      },
      {
        path: "setup/aree",
        element: (
          <RoleGuard allow={[ROLES.ADMIN, ROLES.HR]}>
            <AreePage />
          </RoleGuard>
        ),
      },
      {
        path: "setup/deadlines",
        element: (
          <RoleGuard allow={[ROLES.ADMIN, ROLES.PLANNER, ROLES.PM_RICHIEDENTE, ROLES.RICHIEDENTE, ROLES.HR]}>
            <DateLimitePage />
          </RoleGuard>
        ),
      },
      {
        path: "setup/projects",
        element: (
          <RoleGuard allow={[ROLES.ADMIN, ROLES.PLANNER, ROLES.PM_RICHIEDENTE, ROLES.RICHIEDENTE]}>
            <ProjectsPage />
          </RoleGuard>
        ),
      },
      {
        path: "setup/tasks",
        element: (
          <RoleGuard allow={[ROLES.ADMIN, ROLES.PLANNER, ROLES.PM_RICHIEDENTE, ROLES.RICHIEDENTE]}>
            <TasksPage />
          </RoleGuard>
        ),
      },
      {
        path: "setup/task-links",
        element: (
          <RoleGuard allow={[ROLES.ADMIN, ROLES.PLANNER, ROLES.PM_RICHIEDENTE, ROLES.RICHIEDENTE]}>
            <TaskResourceLinksPage />
          </RoleGuard>
        ),
      },
      {
        path: "setup/task-patterns",
        element: (
          <RoleGuard allow={[ROLES.ADMIN, ROLES.PLANNER]}>
            <TaskResourcePatternsPage />
          </RoleGuard>
        ),
      },
      {
        path: "setup/project-managers",
        element: (
          <RoleGuard allow={[ROLES.ADMIN, ROLES.PLANNER, ROLES.HR]}>
            <ProjectManagersPage />
          </RoleGuard>
        ),
      },
      {
        path: "setup/senior-management",
        element: (
          <RoleGuard allow={[ROLES.ADMIN, ROLES.HR, ROLES.PLANNER]}>
            <SeniorManagementPage />
          </RoleGuard>
        ),
      },
      {
        path: "setup/non-working-days",
        element: (
          <RoleGuard allow={[ROLES.ADMIN, ROLES.PLANNER, ROLES.HR]}>
            <NonWorkingDaysPage />
          </RoleGuard>
        ),
      },
      {
        path: "setup/:table",
        element: (
          <RoleGuard allow={[ROLES.ADMIN, ROLES.PLANNER, ROLES.HR]}>
            <SetupPage />
          </RoleGuard>
        ),
      },
      {
        path: "workbench",
        element: (
          <RoleGuard allow={NON_SALES_BACKOFFICE}>
            <WorkbenchPage />
          </RoleGuard>
        ),
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);

function AuthGate() {
  const { isAuthReady, isAuthenticated, azureEnabled } = useContext(AppContext);

  // Se Azure è disabilitato, l'utente è considerato automaticamente autenticato
  // (salta il controllo isAuthenticated e accede direttamente all'app)
  const hasAccess = azureEnabled ? isAuthenticated : true;

  if (!isAuthReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Fallback />
      </div>
    );
  }

  if (!hasAccess) return <LoginPage />;

  return (
    <PermissionsProvider>
    <ConfigProvider>
      <DataProvider>
        <DeadlineProvider>
          <UiProvider>
            <RouterProvider router={router} />
          </UiProvider>
        </DeadlineProvider>
      </DataProvider>
    </ConfigProvider>
    </PermissionsProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AppProvider>
        <AuthGate />
      </AppProvider>
    </ThemeProvider>
  );
}
